import { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  formatBytes,
  opsService,
  type CleanupPreview,
  type CleanupResult,
  type SchedulerConfig,
  type StorageItem,
} from '../../services/opsService';
import { useAuth } from '../../context/AuthContext';
import { Modal } from '../../components/Modal';

export function InfrastructureStoragePage() {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission('manage_ops') || hasPermission('ops.write');
  const [items, setItems] = useState<StorageItem[]>([]);
  const [preview, setPreview] = useState<CleanupPreview[]>([]);
  const [history, setHistory] = useState<CleanupResult[]>([]);
  const [scheduler, setScheduler] = useState<SchedulerConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [st, pr, hi, sch] = await Promise.all([
        opsService.storage(),
        opsService.cleanupPreview(),
        opsService.cleanupHistory(),
        opsService.getScheduler(),
      ]);
      setItems(Array.isArray(st) ? st : []);
      setPreview(Array.isArray(pr) ? pr : []);
      setHistory(Array.isArray(hi) ? hi : []);
      setScheduler(sch);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const runCleanup = async () => {
    if (!confirmId) return;
    setBusy(true);
    try {
      const res = await opsService.cleanupRun(confirmId);
      if (res.ok) toast.success(`آزاد شد: ${formatBytes(res.reclaimedBytes)}`);
      else toast.error(res.error || 'ناموفق');
      setConfirmId(null);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا');
    } finally {
      setBusy(false);
    }
  };

  const saveScheduler = async () => {
    if (!scheduler || !canWrite) return;
    setBusy(true);
    try {
      setScheduler(await opsService.putScheduler(scheduler));
      toast.success('تنظیمات زمان‌بند ذخیره شد');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">فضای ذخیره و پاکسازی</h1>
          <p className="mt-1 text-sm text-slate-500">
            پاکسازی فقط برای کش بیلد بلااستفاده داکر مجاز است. بکاپ/آپلود/دیتابیس لمس نمی‌شود.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          تازه‌سازی
        </button>
      </div>

      <section className="rounded-xl bg-white p-5 shadow-sm">
        <h2 className="mb-4 font-bold text-slate-900">اقلام ذخیره‌سازی</h2>
        <ul className="divide-y divide-slate-100">
          {items.map((it) => (
            <li key={it.id} className="flex flex-wrap items-center justify-between gap-2 py-3 text-sm">
              <div>
                <div className="font-medium text-slate-900">{it.label}</div>
                {it.path ? <div className="text-xs text-slate-500">{it.path}</div> : null}
                {!it.available ? <div className="text-xs text-amber-700">{it.unavailableReason}</div> : null}
              </div>
              <div className="font-semibold text-slate-800">{it.available ? formatBytes(it.bytes) : '—'}</div>
            </li>
          ))}
        </ul>
      </section>

      <section className="rounded-xl bg-white p-5 shadow-sm">
        <h2 className="mb-4 font-bold text-slate-900">پاکسازی مجاز</h2>
        {preview.map((p) => (
          <div key={p.id} className="flex flex-wrap items-start justify-between gap-4 rounded-lg bg-slate-50 p-4">
            <div>
              <div className="font-semibold text-slate-900">{p.label}</div>
              <p className="mt-1 text-sm text-slate-600">{p.description}</p>
              <p className="mt-1 text-xs text-slate-500">{p.commandSummary}</p>
              <p className="mt-2 text-sm font-medium">تخمین قابل آزادسازی: {formatBytes(p.estimatedBytes)}</p>
              {!p.available ? <p className="mt-1 text-xs text-rose-600">{p.reason}</p> : null}
            </div>
            {canWrite ? (
              <button
                type="button"
                disabled={!p.available || busy}
                onClick={() => setConfirmId(p.id)}
                className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4" />
                اجرای پاکسازی
              </button>
            ) : null}
          </div>
        ))}
      </section>

      {scheduler ? (
        <section className="rounded-xl bg-white p-5 shadow-sm">
          <h2 className="mb-2 font-bold text-slate-900">زمان‌بند خودکار</h2>
          <p className="mb-4 text-sm text-amber-700">به‌صورت پیش‌فرض خاموش است. فقط پس از بررسی صریح فعال کنید.</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={scheduler.enabled}
                disabled={!canWrite}
                onChange={(e) => setScheduler({ ...scheduler, enabled: e.target.checked })}
              />
              فعال‌سازی زمان‌بند
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={scheduler.buildCacheEnabled}
                disabled={!canWrite}
                onChange={(e) => setScheduler({ ...scheduler, buildCacheEnabled: e.target.checked })}
              />
              پاکسازی کش بیلد
            </label>
            <label className="text-sm">
              آستانه دیسک (%)
              <input
                type="number"
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
                value={scheduler.diskThresholdPct}
                disabled={!canWrite}
                onChange={(e) => setScheduler({ ...scheduler, diskThresholdPct: Number(e.target.value) })}
              />
            </label>
            <label className="text-sm">
              حداقل سن کش (ساعت)
              <input
                type="number"
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
                value={scheduler.minCacheAgeHours}
                disabled={!canWrite}
                onChange={(e) => setScheduler({ ...scheduler, minCacheAgeHours: Number(e.target.value) })}
              />
            </label>
          </div>
          {scheduler.lastResult ? (
            <p className="mt-3 text-xs text-slate-500">
              آخرین اجرا: {scheduler.lastRunAt || '—'} · {scheduler.lastResult}
            </p>
          ) : null}
          {canWrite ? (
            <button
              type="button"
              onClick={() => void saveScheduler()}
              disabled={busy}
              className="mt-4 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              ذخیره زمان‌بند
            </button>
          ) : null}
        </section>
      ) : null}

      <section className="rounded-xl bg-white p-5 shadow-sm">
        <h2 className="mb-4 font-bold text-slate-900">تاریخچه پاکسازی</h2>
        {history.length === 0 ? (
          <p className="text-sm text-slate-500">هنوز اجرایی ثبت نشده.</p>
        ) : (
          <ul className="space-y-2 text-sm">
            {history.map((h, i) => (
              <li key={`${h.id}-${i}`} className="rounded-lg bg-slate-50 px-3 py-2">
                <span className={h.ok ? 'text-emerald-700' : 'text-rose-700'}>{h.ok ? 'OK' : 'FAIL'}</span>
                {' · '}
                {formatBytes(h.reclaimedBytes)} · دیسک {h.diskBeforePct.toFixed(1)}% → {h.diskAfterPct.toFixed(1)}%
                {h.initiatedBy ? ` · ${h.initiatedBy}` : ''}
              </li>
            ))}
          </ul>
        )}
      </section>

      <Modal
        isOpen={!!confirmId}
        onClose={() => setConfirmId(null)}
        title="تأیید پاکسازی"
      >
        <p className="text-sm text-slate-600">
          فقط کش بیلد بلااستفاده داکر پاک می‌شود. برای تأیید، ادامه دهید.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" className="rounded-lg border px-4 py-2 text-sm" onClick={() => setConfirmId(null)}>
            انصراف
          </button>
          <button
            type="button"
            disabled={busy}
            className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            onClick={() => void runCleanup()}
          >
            تأیید و اجرا
          </button>
        </div>
      </Modal>
    </div>
  );
}
